import { ALL_LINKS } from "constants/permissions";
import { useFirebase } from "context/firebase.context";
import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * RouteGuard - Checks if user has permission to access the route
 * Redirects to first accessible page if permission denied
 * @param {Object} props
 * @param {string[]} props.permission - Array of permission strings, e.g., ["operator"], ["analyst"], ["operator", "analyst"] (OR logic)
 * @param {React.ReactNode} props.children - Content to render if permission granted
 */
export default function RouteGuard({
  permission = ["operator"],
  children,
}) {
  const { user } = useFirebase();
  const navigate = useNavigate();
  const location = useLocation();
  const [claims, setClaims] = useState(null);
  const [isLoadingClaims, setIsLoadingClaims] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);

  const loadClaims = useCallback(async () => {
    if (user) {
      try {
        const idTokenResult = await user.getIdTokenResult();
        setClaims(idTokenResult.claims);
      } catch (error) {
        console.error("Failed to load claims:", error);
      } finally {
        setIsLoadingClaims(false);
      }
    } else {
      setIsLoadingClaims(false);
    }
  }, [user]);

  useEffect(() => {
    loadClaims();
  }, [loadClaims]);

  // Helper function to check permission
  // permission is an array of strings, e.g., ["operator"], ["analyst"], ["operator", "analyst"]
  // Array means OR logic - user needs at least one of the permissions
  const checkPermission = useCallback((perm, isOperator, isAnalyst) => {
    if (!perm || !Array.isArray(perm) || perm.length === 0) {
      return false;
    }

    // Check if user has at least one of the required permissions (OR logic)
    return perm.some((p) => {
      if (p === "operator") return isOperator;
      if (p === "analyst") return isAnalyst;
      return false;
    });
  }, []);

  useEffect(() => {
    if (isLoadingClaims || !claims) {
      return;
    }

    const isOperator = _.get(claims, "operator", false);
    const isAnalyst = _.get(claims, "analyst", false);

    const hasAccess = checkPermission(permission, isOperator, isAnalyst);
    setHasPermission(hasAccess);

    // If no permission, redirect to first accessible page based on permissions.js
    if (!hasAccess) {
      // Filter links based on user permissions and visibility
      const accessibleLinks = ALL_LINKS.filter((link) =>
        !link.hidden && checkPermission(link.permission, isOperator, isAnalyst)
      );

      const firstAccessiblePath = accessibleLinks.length > 0 ? accessibleLinks[0].path : "/";

      // Only redirect if we're not already on the first accessible page
      if (location.pathname !== firstAccessiblePath) {
        navigate(firstAccessiblePath, { replace: true });
      }
    }
  }, [claims, isLoadingClaims, permission, checkPermission, navigate, location.pathname]);

  if (isLoadingClaims) {
    return null; // Don't render anything while loading
  }

  if (!hasPermission) {
    return null; // Will redirect, don't render content
  }

  return <>{children}</>;
}
